import type { ReactElement } from 'react';
import { useNavigate } from 'react-router';
import type { DiscoverListRow } from '@xdocs/schemas';
import { Button, EmptyState, Mascot, Skeleton, cn, useI18n } from '@xdocs/ui';
import { CategoryFilterChips, useCategoryCatalog } from '../../features/search/index.js';
import { DiscoverCard } from './DiscoverCard.js';
import { useDiscoverList } from './useDiscoverList.js';
import { useDiscoverT } from './useDiscoverT.js';
import styles from './DiscoverPage.module.css';

const SKELETON_CARD_COUNT = 6;

/**
 * `/discover` (F62) — public gallery of approved public shares. Category
 * chips reuse `useCategoryCatalog`/`CategoryFilterChips` (F31, same source
 * as `/search`'s facet — VISUAL note: "categories (search reuse)"); a
 * "Search all documents" link hands full-text search off to `/search`
 * (`discover.list` itself has no `q` — category browse only, F8 owns FTS).
 * Works fully logged out (F62 AC): no `RequireAuth` dependency in this
 * file — see this dir's `WIRING.md` for the router mount point.
 */
export function DiscoverPage(): ReactElement {
  const navigate = useNavigate();
  const { t: tGlobal } = useI18n();
  const { t } = useDiscoverT();
  const { rows, hasMore, categoryId, setCategoryId, isLoading, isLoadingMore, isError, onRetry, onLoadMore } = useDiscoverList();
  const { categories } = useCategoryCatalog();

  function onOpen(row: DiscoverListRow): void {
    void navigate(`/discover/${row.shareId}`);
  }

  const isEmpty = !isLoading && !isError && rows.length === 0;

  return (
    <div className={cn(styles['page'])}>
      <div className={cn(styles['hero'])}>
        <div className={cn(styles['heroContent'])}>
          <Mascot pose="idle" size={64} alt="" />
          <div>
            <p className={cn(styles['eyebrow'])}>{t('discover:page.eyebrow')}</p>
            <h1 className={cn(styles['title'])}>{t('discover:page.title')}</h1>
          </div>
        </div>
        <Button variant="secondary" onClick={() => void navigate('/search')}>
          {t('discover:page.searchCta')}
        </Button>
      </div>

      <CategoryFilterChips categories={categories} activeCategoryId={categoryId} onSelect={setCategoryId} />

      {isError && (
        <EmptyState kind="error" title={t('discover:empty.error.title')} action={{ label: tGlobal('common:action.retry'), onClick: onRetry }} />
      )}

      {!isError && isEmpty && categoryId === null && (
        <EmptyState kind="first-use" title={t('discover:empty.firstUse.title')} description={t('discover:empty.firstUse.description')} />
      )}

      {!isError && isEmpty && categoryId !== null && (
        <EmptyState
          kind="no-results"
          title={t('discover:empty.noResults.title')}
          description={t('discover:empty.noResults.description')}
          action={{ label: tGlobal('common:action.clearFilters'), onClick: () => setCategoryId(null) }}
        />
      )}

      {isLoading && rows.length === 0 && (
        <div className={cn(styles['grid'])} aria-hidden="true">
          <Skeleton shape="card" count={SKELETON_CARD_COUNT} />
        </div>
      )}

      {!isError && rows.length > 0 && (
        <div className={cn(styles['grid'])} role="list" aria-label={t('discover:page.title')}>
          {rows.map((row) => (
            <div role="listitem" key={row.shareId}>
              <DiscoverCard row={row} onOpen={onOpen} />
            </div>
          ))}
        </div>
      )}

      {!isError && hasMore && (
        <div className={cn(styles['loadMore'])}>
          <Button variant="secondary" state={isLoadingMore ? 'loading' : 'idle'} onClick={onLoadMore}>
            {tGlobal('common:action.loadMore')}
          </Button>
        </div>
      )}
    </div>
  );
}
